import React, { useState } from 'react';
import { Code, Copy, CheckCircle, MessageCircle, Zap, Server, Key, HelpCircle } from 'lucide-react';

interface A2AIntegrationGuideProps {
  onOpenAssistant: () => void;
  onSetAssistantInitialMessage: (message: string) => void;
}

export function A2AIntegrationGuide({ onOpenAssistant, onSetAssistantInitialMessage }: A2AIntegrationGuideProps) {
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const copyToClipboard = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const handleAskAssistant = (question: string) => {
    onSetAssistantInitialMessage(question);
    onOpenAssistant();
  };

  const a2aRegistrationCode = `// Register your agent with the A2A protocol
async function registerWithA2A(agentId) {
  try {
    const response = await fetch(
      \`\${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/register\`,
      {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${import.meta.env.VITE_SUPABASE_ANON_KEY}\`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: agentId,
          endpoint: "https://your-agent-domain.com/api/a2a-endpoint",
          capabilities: ["text_generation", "data_analysis", "question_answering"]
        })
      }
    );

    const data = await response.json();
    
    if (data.success) {
      console.log("A2A registration successful!");
      console.log("A2A Key:", data.a2a_key);
      // Store this key securely - it will be needed for all A2A communications
      return data.a2a_key;
    } else {
      throw new Error(data.error || "Registration failed");
    }
  } catch (error) {
    console.error("A2A registration error:", error);
    throw error;
  }
}`;

  const a2aEndpointCode = `// Example Express.js endpoint to receive A2A messages
app.post('/api/a2a-endpoint', async (req, res) => {
  try {
    const message = req.body;
    
    // Validate the message format
    if (!message.sender || !message.content || !message.message_type) {
      return res.status(400).json({
        success: false,
        error: 'Invalid message format'
      });
    }
    
    console.log(\`Received A2A message from \${message.sender.name} (\${message.sender.agent_id})\`);
    
    // Process different message types
    if (message.message_type === 'request') {
      // Extract the requested action from content
      const { action, ...params } = message.content;
      
      let result;
      // Handle different actions
      switch (action) {
        case 'generate_text':
          result = await generateText(params.prompt, params.options);
          break;
        case 'analyze_data':
          result = await analyzeData(params.data);
          break;
        case 'answer_question':
          result = await answerQuestion(params.question, params.context);
          break;
        default:
          throw new Error(\`Unsupported action: \${action}\`);
      }
      
      // Send response back
      return res.json({
        success: true,
        sender: {
          agent_id: "your-agent-id", // Your agent's ID
          name: "Your Agent Name"
        },
        recipient: message.sender,
        message_type: "response",
        content: {
          result
        },
        metadata: {
          request_id: message.metadata?.request_id,
          timestamp: new Date().toISOString()
        }
      });
    } 
    else if (message.message_type === 'notification') {
      // Handle notification (no response needed)
      processNotification(message.content);
      return res.json({ success: true });
    }
    else {
      // Handle other message types
      return res.json({ 
        success: true,
        message: "Message received"
      });
    }
  } catch (error) {
    console.error("Error processing A2A message:", error);
    return res.status(500).json({
      success: false,
      error: error.message || "Internal server error"
    });
  }
});

// Example implementation functions
async function generateText(prompt, options = {}) {
  // Your text generation logic here
  return { text: "Generated text based on prompt: " + prompt };
}

async function analyzeData(data) {
  // Your data analysis logic here
  return { 
    analysis: "Data analysis results",
    summary: "Summary of findings"
  };
}

async function answerQuestion(question, context = null) {
  // Your question answering logic here
  return { answer: "Answer to: " + question };
}

function processNotification(content) {
  // Process notification, no response needed
  console.log("Notification received:", content);
}`;

  const a2aSendMessageCode = `// Send a message to another agent
async function sendA2AMessage(recipientAgentId, content, messageType = 'request') {
  try {
    const response = await fetch(
      \`\${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/send\`,
      {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${import.meta.env.VITE_SUPABASE_ANON_KEY}\`,
          'Content-Type': 'application/json',
          'X-A2A-Key': 'your-a2a-key' // The key you received during registration
        },
        body: JSON.stringify({
          sender: {
            agent_id: "your-agent-id", // Your agent's ID
            name: "Your Agent Name"
          },
          recipient: {
            agent_id: recipientAgentId
          },
          message_type: messageType,
          content: content,
          metadata: {
            request_id: \`req-\${Date.now()}-\${Math.random().toString(36).substr(2, 9)}\`,
            timestamp: new Date().toISOString(),
            priority: "normal"
          }
        })
      }
    );

    const data = await response.json();
    
    if (data.success) {
      console.log("A2A message sent successfully!");
      return data.result;
    } else {
      throw new Error(data.error || "Failed to send A2A message");
    }
  } catch (error) {
    console.error("A2A message error:", error);
    throw error;
  }
}

// Example usage
async function askDataAnalysisAgent() {
  try {
    const result = await sendA2AMessage(
      "data-analysis-agent-id",
      {
        action: "analyze_data",
        data: [
          { month: "Jan", sales: 1000 },
          { month: "Feb", sales: 1200 },
          { month: "Mar", sales: 900 }
        ]
      }
    );
    
    console.log("Analysis result:", result);
    return result;
  } catch (error) {
    console.error("Failed to get data analysis:", error);
  }
}`;

  const a2aDiscoverCode = `// Discover agents with specific capabilities
async function discoverAgents(capabilities = []) {
  try {
    const response = await fetch(
      \`\${import.meta.env.VITE_SUPABASE_URL}/functions/v1/a2a-protocol-handler/discover\`,
      {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${import.meta.env.VITE_SUPABASE_ANON_KEY}\`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          capabilities: capabilities
        })
      }
    );

    const data = await response.json();
    
    if (data.success) {
      console.log(\`Found \${data.agents.length} agents with requested capabilities\`);
      return data.agents;
    } else {
      throw new Error(data.error || "Discovery failed");
    }
  } catch (error) {
    console.error("A2A discovery error:", error);
    throw error;
  }
}

// Example: Find agents that can perform data visualization
async function findVisualizationAgents() {
  const agents = await discoverAgents(["data_visualization"]);
  console.log("Available visualization agents:", agents);
  return agents;
}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
        <div className="flex items-center gap-3">
          <Zap className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">A2A Integration Guide</h2>
            <p className="opacity-90">Implement Agent-to-Agent Communication</p>
          </div>
        </div>
      </div>
      
      <div className="p-6 space-y-8">
        {/* Introduction */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Getting Started with A2A</h3>
          <p className="text-gray-700 mb-4">
            The Agent-to-Agent (A2A) protocol enables your AI agents to communicate with each other, creating powerful multi-agent systems. This guide will walk you through implementing A2A in your agents.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <Key className="w-5 h-5" />
              <span className="font-medium">Prerequisites</span>
            </div>
            <ul className="text-blue-700 text-sm space-y-1">
              <li>• A deployed agent with an accessible HTTP endpoint</li>
              <li>• Your agent's unique ID from the Agent Store</li>
              <li>• Basic understanding of RESTful APIs</li>
            </ul>
          </div>
        </section>
        
        {/* Step 1: Registration */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 1: Register with A2A Protocol</h3>
          <p className="text-gray-700 mb-4">
            First, register your agent with the A2A protocol handler to receive an A2A key. This key will be used to authenticate your agent when sending messages.
          </p>
          <div className="relative">
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-blue-400 text-sm">{a2aRegistrationCode}</pre>
            </div>
            <button
              onClick={() => copyToClipboard(a2aRegistrationCode, 'registration')}
              className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
              title="Copy to clipboard"
            >
              {copiedSection === 'registration' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => handleAskAssistant("How do I securely store and manage my A2A API key?")}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <HelpCircle className="w-4 h-4" />
              Ask about API key management
            </button>
          </div>
        </section>
        
        {/* Step 2: Create Endpoint */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 2: Create A2A Endpoint</h3>
          <p className="text-gray-700 mb-4">
            Implement an endpoint in your agent to receive and process A2A messages from other agents.
          </p>
          <div className="relative">
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-green-400 text-sm">{a2aEndpointCode}</pre>
            </div>
            <button
              onClick={() => copyToClipboard(a2aEndpointCode, 'endpoint')}
              className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
              title="Copy to clipboard"
            >
              {copiedSection === 'endpoint' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => handleAskAssistant("What are the best practices for validating and processing incoming A2A messages?")}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <HelpCircle className="w-4 h-4" />
              Ask about message processing
            </button>
          </div>
        </section>
        
        {/* Step 3: Send Messages */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 3: Send Messages to Other Agents</h3>
          <p className="text-gray-700 mb-4">
            Use the A2A protocol handler to send messages to other agents and process their responses.
          </p>
          <div className="relative">
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-purple-400 text-sm">{a2aSendMessageCode}</pre>
            </div>
            <button
              onClick={() => copyToClipboard(a2aSendMessageCode, 'send')}
              className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
              title="Copy to clipboard"
            >
              {copiedSection === 'send' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => handleAskAssistant("How can I handle errors and retries when sending A2A messages?")}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <HelpCircle className="w-4 h-4" />
              Ask about error handling
            </button>
          </div>
        </section>
        
        {/* Step 4: Discover Agents */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 4: Discover Other Agents</h3>
          <p className="text-gray-700 mb-4">
            Use the discovery mechanism to find other agents with specific capabilities that your agent can collaborate with.
          </p>
          <div className="relative">
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-orange-400 text-sm">{a2aDiscoverCode}</pre>
            </div>
            <button
              onClick={() => copyToClipboard(a2aDiscoverCode, 'discover')}
              className="absolute top-3 right-3 p-2 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors"
              title="Copy to clipboard"
            >
              {copiedSection === 'discover' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => handleAskAssistant("What are some common capabilities I should implement in my agent for A2A compatibility?")}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <HelpCircle className="w-4 h-4" />
              Ask about agent capabilities
            </button>
          </div>
        </section>
        
        {/* Best Practices */}
        <section>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Best Practices</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Message Design</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Keep message content concise and structured</li>
                <li>• Use consistent action names across agents</li>
                <li>• Include all necessary parameters in requests</li>
                <li>• Provide meaningful error messages</li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Error Handling</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Implement exponential backoff for retries</li>
                <li>• Set appropriate timeouts for requests</li>
                <li>• Log all A2A communication for debugging</li>
                <li>• Handle network failures gracefully</li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Security</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Validate all incoming messages</li>
                <li>• Implement rate limiting on your endpoint</li>
                <li>• Use HTTPS for all communications</li>
                <li>• Rotate A2A keys periodically</li>
              </ul>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Performance</h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Optimize endpoint response times</li>
                <li>• Implement caching where appropriate</li>
                <li>• Use asynchronous processing for long-running tasks</li>
                <li>• Monitor A2A traffic and resource usage</li>
              </ul>
            </div>
          </div>
        </section>
        
        {/* Get Help */}
        <section>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
            <h3 className="text-lg font-semibold text-purple-900 mb-3">Need More Help?</h3>
            <p className="text-purple-700 mb-4">
              Our AI assistant can provide personalized guidance on implementing A2A in your specific agent.
            </p>
            <button
              onClick={() => handleAskAssistant("I'm implementing A2A protocol in my agent. Can you provide a step-by-step guide for my specific use case? I want to create an agent that can communicate with other agents to perform complex tasks.")}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Get Personalized A2A Help
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}